import { useCallback, useMemo } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { SearchPromiseClient } from '@trustero/trustero-api-web/lib/search/search_grpc_web_pb'
import {
  SearchOptions,
  SearchRequest,
  SearchResponse,
} from '@trustero/trustero-api-web/lib/search/search_pb'
import { GrpcResponse, NewGrpcResponse } from 'src/components/async/hooks/types'
import { useSwrImmutableGrpc } from 'src/components/async/useSwrImmutableGrpc'
import { useNavigateContext } from 'src/components/Reusable/Navigation/NavigateContext'
import { usePublicTrustPortalAccount } from 'src/app/App.hooks'
import { PublicTrustServicePromiseClient } from '@trustero/trustero-api-web/lib/trust/trust_grpc_web_pb'
import { SearchTrustPortalContentRequest } from '@trustero/trustero-api-web/lib/trust/trust_pb'
import queryString from 'query-string'
import { toQuickSearchText, useQuickSearchQuery } from './quickSearch'
import {
  AdvancedSearchOptions,
  ContentSearchInput,
  SearchableData,
  SearchResults,
  TrustPortalContentSearchInput,
} from './search.types'
import {
  getSearchQueryString,
  parseSearchQuery,
  performSearch,
} from './search.helpers'

export const useSearch = (
  paramPrefix?: string,
): ((
  query: string,
  advancedSearch?: AdvancedSearchOptions,
  contentSearch?: boolean,
) => void) => {
  const location = useLocation()
  const searchParamsFallback = queryString.parse(location.search, {
    arrayFormat: 'bracket',
  })
  const navigateFallback = useNavigate()
  const {
    navigateState: {
      navigate,
      parsedQueryParams,
      locationSearchString: contextLocationSearchString,
    },
  } = useNavigateContext()
  const search = useCallback(
    (
      query: string,
      advancedSearch?: AdvancedSearchOptions,
      contentSearch?: boolean,
    ) => {
      const searchParams = parsedQueryParams || searchParamsFallback
      const locationSearchString =
        contextLocationSearchString || location.search
      const navigateFunc = navigate || navigateFallback
      const paramName = paramPrefix ? `${paramPrefix}Search` : 'search'
      const contentParamName = paramPrefix
        ? `${paramPrefix}ContentSearch`
        : 'contentSearch'
      if (!advancedSearch) {
        if (!query.trim()) {
          navigateFunc(
            queryString.exclude(`${location.pathname}${locationSearchString}`, [
              paramName,
            ]),
          )
          return
        }
      }
      const searchParamValue = getSearchQueryString(query, advancedSearch)
      const updatedParams: queryString.StringifiableRecord = {
        ...searchParams,
        [paramName]: encodeURIComponent(searchParamValue),
      }
      if (contentSearch) {
        updatedParams[contentParamName] = 'true'
      }
      if (searchParams['page']) {
        updatedParams['page'] = 1
      }
      const updatedHref = searchParamValue
        ? queryString.stringifyUrl(
            {
              url: location.pathname,
              query: updatedParams,
            },
            { arrayFormat: 'bracket' },
          )
        : queryString.exclude(`${location.pathname}${locationSearchString}`, [
            paramName,
            contentParamName,
          ])
      navigateFunc(updatedHref)
    },
    [
      navigate,
      navigateFallback,
      parsedQueryParams,
      searchParamsFallback,
      contextLocationSearchString,
      location,
      paramPrefix,
    ],
  )

  return search
}

export const useContentSearch = ({
  searchOptions,
  contentIds,
  shouldFetch = true,
}: {
  searchOptions: SearchOptions
  contentIds: string[]
  shouldFetch?: boolean
}): GrpcResponse<SearchResponse> => {
  const request = new SearchRequest()
    .setSearchOptions(searchOptions)
    .setContentIdsList(contentIds)
  const { response } = useSwrImmutableGrpc(
    SearchPromiseClient.prototype.search,
    request,
    shouldFetch,
  )
  return NewGrpcResponse(response)
}

export const useTrustPortalContentSearch = ({
  searchOptions,
  contentIds,
  shouldFetch = true,
  trustPortalAccountId,
}: {
  searchOptions: SearchOptions
  contentIds: string[]
  trustPortalAccountId: string
  shouldFetch?: boolean
}): GrpcResponse<SearchResponse> => {
  const request = new SearchTrustPortalContentRequest()
    .setSearchOptions(searchOptions)
    .setContentIdsList(contentIds)
    .setTrustPortalAccountId(trustPortalAccountId)
  const { response } = useSwrImmutableGrpc(
    PublicTrustServicePromiseClient.prototype.searchTrustPortalContent,
    request,
    shouldFetch,
  )
  return NewGrpcResponse(response)
}

export const useSearchQuery = (paramPrefix?: string): string => {
  const location = useLocation()
  const searchParamsFallback = queryString.parse(location.search, {
    arrayFormat: 'bracket',
  })
  const {
    navigateState: { parsedQueryParams },
  } = useNavigateContext()
  const paramName = paramPrefix ? `${paramPrefix}Search` : 'search'
  const searchParams = parsedQueryParams || searchParamsFallback
  const query = searchParams[paramName] as string
  const decodedQuery = query ? decodeURIComponent(query) : ''
  return decodedQuery
}

export const useIsContentSearchActive = (paramPrefix?: string): boolean => {
  const location = useLocation()
  const searchParamsFallback = queryString.parse(location.search, {
    arrayFormat: 'bracket',
  })
  const {
    navigateState: { parsedQueryParams },
  } = useNavigateContext()
  const contentParamName = paramPrefix
    ? `${paramPrefix}ContentSearch`
    : 'contentSearch'
  const searchParams = parsedQueryParams || searchParamsFallback
  const contentSearch = (searchParams[contentParamName] as string) || 'false'
  return contentSearch === 'true'
}

export const useApplySearch = <T>(
  items: SearchableData<T>[],
  paramPrefix?: string,
  tempSearchQuery?: string,
): SearchResults<T> => {
  const urlQuery = useSearchQuery(paramPrefix)
  // While text is typed and not yet searched, lists filter on it as plain text, with
  // no query syntax and no content search; Enter runs the full search.
  const quickQuery = useQuickSearchQuery(paramPrefix)
  const isQuickSearch = !tempSearchQuery && quickQuery !== null
  const query = isQuickSearch
    ? toQuickSearchText(quickQuery)
    : tempSearchQuery || urlQuery
  const isContentSearchActive =
    useIsContentSearchActive(paramPrefix) && !isQuickSearch

  const searchOptions = useMemo(
    () =>
      isQuickSearch
        ? new SearchOptions().setExactMatchStrsList(query ? [query] : [])
        : parseSearchQuery(query),
    [query, isQuickSearch],
  )

  const contentIds = useMemo(
    () =>
      items
        .map((item) => item.contentIds)
        .filter((ids) => ids !== undefined)
        .flat() as string[],
    [items],
  )

  const contentSearchInput = useMemo(() => {
    return {
      searchOptions,
      contentIds,
      shouldFetch: isContentSearchActive && query.trim() !== '',
    } as ContentSearchInput
  }, [searchOptions, isContentSearchActive, query, contentIds])
  const { data: contentSearchResponse } = useContentSearch(contentSearchInput)

  return useMemo(() => {
    return performSearch(items, searchOptions, contentSearchResponse)
  }, [items, searchOptions, contentSearchResponse])
}

export const useApplyTrustPortalSearch = <T>(
  items: SearchableData<T>[],
  paramPrefix?: string,
  tempSearchQuery?: string,
): SearchResults<T> => {
  const { trustAccountId } = usePublicTrustPortalAccount()
  const urlQuery = useSearchQuery(paramPrefix)
  const query = tempSearchQuery || urlQuery

  // parse the query
  const searchOptions = useMemo(() => parseSearchQuery(query), [query])

  const contentIds = useMemo(
    () =>
      items
        .map((item) => item.contentIds)
        .filter((ids) => ids !== undefined)
        .flat() as string[],
    [items],
  )

  const contentSearchInput = useMemo(() => {
    return {
      searchOptions,
      contentIds,
      shouldFetch: query.trim() !== '',
      trustPortalAccountId: trustAccountId,
    } as TrustPortalContentSearchInput
  }, [searchOptions, query, trustAccountId, contentIds])

  const { data: contentSearchResponse } =
    useTrustPortalContentSearch(contentSearchInput)

  return useMemo(() => {
    return performSearch(items, searchOptions, contentSearchResponse)
  }, [items, searchOptions, contentSearchResponse])
}
